/* Küçük bir şablon motoru — üretici bunun dışında bir şeye ihtiyaç duymuyor.
   Sayfa iskeletleri elle yazılmış HTML olarak kalsın, yalnızca tekrar eden
   bloklar döngüye dönüşsün diye var (plan §7.1). Mustache'in tanıdık
   alt kümesi: değer, ham değer, parça, döngü, koşul. */

export type Scope = Record<string, unknown>;
export type Partials = Record<string, string>;

/** HTML'e gömülen metin. Elle yazılmış sayfa `&amp;` kullanıyor; aynısı üretilir. */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Öznitelik değeri — metin kaçışıyla aynı kural, tek tırnak da kapatılır. */
export function escapeAttr(value: string): string {
  return escapeHtml(value).replace(/'/g, '&#39;');
}

type Frame = { data: unknown; meta: Scope };

function lookup(stack: Frame[], path: string): unknown {
  const trimmed = path.trim();
  if (trimmed === '.') return stack[stack.length - 1]?.data;

  if (trimmed.startsWith('@')) {
    for (let i = stack.length - 1; i >= 0; i--) {
      const meta = stack[i]!.meta;
      if (trimmed in meta) return meta[trimmed];
    }
    return undefined;
  }

  const parts = trimmed.split('.');
  for (let i = stack.length - 1; i >= 0; i--) {
    let cursor: unknown = stack[i]!.data;
    let ok = true;
    for (const part of parts) {
      if (cursor === null || typeof cursor !== 'object' || !(part in cursor)) {
        ok = false;
        break;
      }
      cursor = (cursor as Record<string, unknown>)[part];
    }
    if (ok) return cursor;
  }
  return undefined;
}

function truthy(value: unknown): boolean {
  if (Array.isArray(value)) return value.length > 0;
  return value !== undefined && value !== null && value !== false && value !== '';
}

function stringify(value: unknown): string {
  if (value === undefined || value === null || value === false) return '';
  return String(value);
}

/* Yalnız başına bir satırda duran blok etiketi, satırıyla birlikte silinir —
   yoksa her döngü çıktıya boş satır bırakır. */
const STANDALONE = /^[ \t]*(\{\{[#/^][^}]*\}\}|\{\{>\s*[\w.-]+\s*\}\})[ \t]*\r?\n/gm;

type Node =
  | { kind: 'text'; value: string }
  | { kind: 'value'; path: string; raw: boolean }
  | { kind: 'partial'; name: string }
  | { kind: 'block'; tag: 'each' | 'if' | 'unless'; path: string; body: Node[]; alt: Node[] };

const TOKEN = /\{\{([{#/^>]?)\s*([^{}]*?)\s*\}?\}\}/g;

function parse(source: string): Node[] {
  const root: Node[] = [];
  const stack: { node: Extract<Node, { kind: 'block' }>; list: Node[] }[] = [];
  let out = root;
  let last = 0;

  const push = (node: Node) => out.push(node);
  const text = (value: string) => {
    if (value) push({ kind: 'text', value });
  };

  for (const m of source.matchAll(TOKEN)) {
    text(source.slice(last, m.index));
    last = m.index + m[0].length;
    const sigil = m[1] ?? '';
    const body = m[2] ?? '';

    if (sigil === '{') {
      push({ kind: 'value', path: body, raw: true });
    } else if (sigil === '>') {
      push({ kind: 'partial', name: body.trim() });
    } else if (sigil === '#') {
      const [tag, ...rest] = body.split(/\s+/);
      if (tag !== 'each' && tag !== 'if' && tag !== 'unless') {
        throw new Error(`bilinmeyen blok: ${tag}`);
      }
      const node: Extract<Node, { kind: 'block' }> = {
        kind: 'block',
        tag,
        path: rest.join(' '),
        body: [],
        alt: []
      };
      push(node);
      stack.push({ node, list: out });
      out = node.body;
    } else if (sigil === '^') {
      const top = stack[stack.length - 1];
      if (!top) throw new Error('{{^}} bir blok içinde değil');
      out = top.node.alt;
    } else if (sigil === '/') {
      const top = stack.pop();
      if (!top) throw new Error(`kapanmamış blok: ${body}`);
      out = top.list;
    } else {
      push({ kind: 'value', path: body, raw: false });
    }
  }
  text(source.slice(last));

  if (stack.length) throw new Error(`kapatılmamış blok: ${stack[stack.length - 1]!.node.path}`);
  return root;
}

function run(nodes: Node[], stack: Frame[], partials: Partials): string {
  let out = '';
  for (const node of nodes) {
    switch (node.kind) {
      case 'text':
        out += node.value;
        break;
      case 'value': {
        const value = stringify(lookup(stack, node.path));
        out += node.raw ? value : escapeHtml(value);
        break;
      }
      case 'partial': {
        const source = partials[node.name];
        if (source === undefined) throw new Error(`parça bulunamadı: ${node.name}`);
        out += run(parse(strip(source)), stack, partials);
        break;
      }
      case 'block': {
        const value = lookup(stack, node.path);
        if (node.tag === 'each') {
          const items = Array.isArray(value) ? value : [];
          if (!items.length) {
            out += run(node.alt, stack, partials);
            break;
          }
          items.forEach((item, index) => {
            stack.push({
              data: item,
              meta: {
                '@index': index,
                '@n': index + 1,
                '@pad': String(index + 1).padStart(2, '0'),
                '@first': index === 0,
                '@last': index === items.length - 1,
                '@count': items.length
              }
            });
            out += run(node.body, stack, partials);
            stack.pop();
          });
        } else {
          const hit = node.tag === 'if' ? truthy(value) : !truthy(value);
          out += run(hit ? node.body : node.alt, stack, partials);
        }
        break;
      }
    }
  }
  return out;
}

const strip = (source: string) => source.replace(STANDALONE, '$1');

export function render(source: string, scope: Scope, partials: Partials = {}): string {
  return run(parse(strip(source)), [{ data: scope, meta: {} }], partials);
}
